import React, { useEffect, useState, useCallback } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Table,
  Input,
  Menu,
  Label,
  Loader,
  Button,
  Pagination,
  Icon,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../../api';

const PAGE_SIZE = 50;

const STATUS_COLORS = {
  draft: 'yellow',
  in_review: 'orange',
  effective: 'green',
  superseded: 'grey',
  obsolete: 'red',
};

const getDocCategory = (type) => {
  if (['POL', 'SOP', 'WI', 'FRM', 'TECH'].includes(type))
    return { label: 'Controlled', color: '#0070f3' };
  if (type === 'EXT') return { label: 'External', color: '#9333ea' };
  return { label: 'Record', color: '#6b7280' };
};

export default function DocumentRegister() {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [domain, setDomain] = useState(params.get('domain') || '');
  const [type, setType] = useState(params.get('type') || '');
  const [status, setStatus] = useState(params.get('status') || '');
  const [search, setSearch] = useState(params.get('search') || '');
  const [searchInput, setSearchInput] = useState(params.get('search') || '');
  const [page, setPage] = useState(parseInt(params.get('page'), 10) || 1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (domain) qs.set('domain', domain);
    if (type) qs.set('type', type);
    if (status) qs.set('status', status);
    if (search) qs.set('search', search);
    qs.set('page', page);
    qs.set('page_size', PAGE_SIZE);

    fetchApi(`${getApiPath()}/@qms/register?${qs}`)
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [domain, type, status, search, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sync URL
  useEffect(() => {
    const qs = new URLSearchParams();
    if (domain) qs.set('domain', domain);
    if (type) qs.set('type', type);
    if (status) qs.set('status', status);
    if (search) qs.set('search', search);
    if (page > 1) qs.set('page', page);
    const str = qs.toString();
    history.replace(`/qms/register${str ? `?${str}` : ''}`);
  }, [domain, type, status, search, page, history]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const clearFilters = () => {
    setDomain('');
    setType('');
    setStatus('');
    setSearch('');
    setSearchInput('');
    setPage(1);
  };

  const totalPages = data
    ? Math.ceil(data.items_total / PAGE_SIZE)
    : 0;

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em' }}>
      <Helmet title="Document Register" />
      <Link to="/qms">&larr; QMS Dashboard</Link>

      <div className="polyglot-page-header">
        <h1>Document Register</h1>
        <p className="subtitle">
          {data ? `${data.items_total} controlled documents` : ''}
        </p>
      </div>

      <Segment secondary size="small" style={{ marginBottom: '1em' }}>
        <Icon name="archive" /> Also see the{' '}
        <Link to="/docs"><strong>Full Knowledge Base</strong></Link>
        {' '}for all project documentation, guides, and reference material.
      </Segment>

      <div style={{ display: 'flex', gap: '1.5em' }}>
        {/* Sidebar filters */}
        <div style={{ width: '200px', flexShrink: 0 }}>
          {/* Domain filter */}
          <Header as="h5" style={{ marginBottom: '0.3em' }}>
            Domain
          </Header>
          <Menu vertical fluid size="small">
            <Menu.Item
              active={!domain}
              onClick={() => {
                setDomain('');
                setPage(1);
              }}
            >
              All domains
            </Menu.Item>
            {(data?.domains || [])
              .filter((d) => d.doc_count > 0)
              .map((d) => (
                <Menu.Item
                  key={d.code}
                  active={domain === d.code}
                  onClick={() => {
                    setDomain(d.code);
                    setPage(1);
                  }}
                >
                  {d.label}
                  <Label size="tiny">{d.doc_count}</Label>
                </Menu.Item>
              ))}
          </Menu>

          {/* Type filter */}
          <Header as="h5" style={{ marginBottom: '0.3em', marginTop: '1em' }}>
            Type
          </Header>
          <Menu vertical fluid size="small">
            <Menu.Item
              active={!type}
              onClick={() => {
                setType('');
                setPage(1);
              }}
            >
              All types
            </Menu.Item>
            {(data?.types || [])
              .filter((t) => t.doc_count > 0)
              .map((t) => (
                <Menu.Item
                  key={t.code}
                  active={type === t.code}
                  onClick={() => {
                    setType(t.code);
                    setPage(1);
                  }}
                >
                  {t.label}
                  <Label size="tiny">{t.doc_count}</Label>
                </Menu.Item>
              ))}
          </Menu>

          {/* Status filter */}
          <Header as="h5" style={{ marginBottom: '0.3em', marginTop: '1em' }}>
            Status
          </Header>
          <Menu vertical fluid size="small">
            <Menu.Item
              active={!status}
              onClick={() => {
                setStatus('');
                setPage(1);
              }}
            >
              All statuses
            </Menu.Item>
            {(data?.statuses || [])
              .filter((s) => s.doc_count > 0)
              .map((s) => (
                <Menu.Item
                  key={s.code}
                  active={status === s.code}
                  onClick={() => {
                    setStatus(s.code);
                    setPage(1);
                  }}
                >
                  {s.label}
                  <Label
                    size="tiny"
                    color={STATUS_COLORS[s.code] || 'grey'}
                  >
                    {s.doc_count}
                  </Label>
                </Menu.Item>
              ))}
          </Menu>

          {(domain || type || status || search) && (
            <Button
              fluid
              size="tiny"
              style={{ marginTop: '1em' }}
              onClick={clearFilters}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Search bar */}
          <Segment secondary size="small" style={{ marginBottom: '1em' }}>
            <Input
              fluid
              icon="search"
              placeholder="Search documents..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              action={{
                content: 'Search',
                onClick: handleSearch,
              }}
            />
          </Segment>

          {loading ? (
            <Loader active inline="centered" />
          ) : error ? (
            <Segment color="red">Error: {error}</Segment>
          ) : (
            <>
              <Table compact celled selectable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>ID</Table.HeaderCell>
                    <Table.HeaderCell>Title</Table.HeaderCell>
                    <Table.HeaderCell>Type</Table.HeaderCell>
                    <Table.HeaderCell>Domain</Table.HeaderCell>
                    <Table.HeaderCell>Version</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Owner</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {(data.items || []).map((doc) => (
                    <Table.Row key={doc.document_id}>
                      <Table.Cell>
                        <Link
                          to={`/qms/register/${encodeURIComponent(doc.document_id)}`}
                        >
                          <strong>{doc.document_id}</strong>
                        </Link>
                      </Table.Cell>
                      <Table.Cell>{doc.title}</Table.Cell>
                      <Table.Cell>
                        {doc.document_type}
                        {' '}
                        {(() => {
                          const cat = getDocCategory(doc.document_type);
                          return (
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '0.1em 0.4em',
                                borderRadius: '3px',
                                fontSize: '0.75em',
                                fontWeight: 600,
                                color: '#fff',
                                backgroundColor: cat.color,
                              }}
                            >
                              {cat.label}
                            </span>
                          );
                        })()}
                      </Table.Cell>
                      <Table.Cell>{doc.domain_code}</Table.Cell>
                      <Table.Cell>{doc.version}</Table.Cell>
                      <Table.Cell>
                        <Label
                          size="tiny"
                          color={STATUS_COLORS[doc.status] || 'grey'}
                        >
                          {doc.status}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>{doc.owner}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>

              {totalPages > 1 && (
                <div style={{ textAlign: 'center', marginTop: '1em' }}>
                  <Pagination
                    activePage={page}
                    totalPages={totalPages}
                    onPageChange={(e, { activePage }) => setPage(activePage)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Container>
  );
}
